/**
 * Portal-owned Creator attribution bridge.
 *
 * A verified Creator link records an eligible per-Throwback touch in Portal,
 * then places only Portal's signed opaque token in Shopify's cart attribute.
 * Returning tracked browsers are recognized by a non-sensitive server-set
 * hint cookie; ordinary shoppers make no Portal request.
 *
 * This file never calculates rewards, never puts a raw token in analytics,
 * and never uses IP, fingerprinting, or probabilistic attribution.
 */
(function () {
  'use strict';

  if (window.__tbbCreatorAttributionLoaded) return;
  window.__tbbCreatorAttributionLoaded = true;

  var PORTAL = 'https://portal.throwbackbuys.com';
  var REF_KEY = 'ref';
  var HINT_COOKIE = 'tbb_attr_active';
  var CART_ATTRIBUTE = '__tbb_attribution';
  var EVENT_TYPES = {
    product_view: true,
    cart_start: true,
    checkout_start: true
  };
  var nativeFetch = window.fetch.bind(window);
  var active = null;
  var initialization = null;
  var emitted = {};

  function hasHint() {
    return new RegExp('(?:^|;\\s*)' + HINT_COOKIE + '=1(?:;|$)').test(document.cookie || '');
  }

  function refCode() {
    try { return new URL(window.location.href).searchParams.get(REF_KEY) || ''; }
    catch (e) { return ''; }
  }

  function productId() {
    try {
      var product = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
      return product && product.id ? String(product.id) : '';
    } catch (e) { return ''; }
  }

  function landing() {
    var id = productId();
    if (/\/products\//.test(window.location.pathname) && id) {
      return { landingKind: 'product', productId: id };
    }
    if (/\/collections\//.test(window.location.pathname)) {
      return { landingKind: 'capsule' };
    }
    return null;
  }

  function portal(path, options) {
    options = options || {};
    var init = {
      method: options.method || 'GET',
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    };
    if (options.body) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(options.body);
    }
    if (options.keepalive) init.keepalive = true;
    return nativeFetch(PORTAL + path, init).then(function (response) {
      return response.json().catch(function () { return null; }).then(function (json) {
        return response.ok && json ? json : null;
      });
    });
  }

  function cartAttributeFrom(payload) {
    var attribute = payload && payload.cartAttribute;
    if (!attribute || attribute.key !== CART_ATTRIBUTE || typeof attribute.value !== 'string' || !attribute.value) {
      return null;
    }
    return attribute;
  }

  function writeCartAttribute(attribute) {
    var attributes = {};
    attributes[attribute.key] = attribute.value;
    return nativeFetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (response) {
      return response.ok;
    });
  }

  function activate(payload) {
    var attribute = cartAttributeFrom(payload);
    if (!attribute) return null;
    active = {
      creatorHandle: typeof payload.creatorHandle === 'string' ? payload.creatorHandle : '',
      browserClass: typeof payload.browserClass === 'string' ? payload.browserClass : '',
      productId: productId()
    };
    return writeCartAttribute(attribute).then(function () { return active; });
  }

  function refreshSession() {
    return portal('/api/creator/attribution/session').then(activate);
  }

  function start() {
    if (initialization) return initialization;
    var ref = refCode();
    var context = landing();
    if (ref && context) {
      initialization = portal('/api/creator/attribution/touch', {
        method: 'POST',
        body: {
          refCode: ref,
          landingKind: context.landingKind,
          productId: context.productId
        }
      }).then(function (payload) {
        if (cartAttributeFrom(payload)) return activate(payload);
        return hasHint() ? refreshSession() : null;
      }).catch(function () {
        return hasHint() ? refreshSession().catch(function () { return null; }) : null;
      });
    } else if (hasHint()) {
      initialization = refreshSession().catch(function () { return null; });
    } else {
      initialization = Promise.resolve(null);
    }
    initialization.then(function (session) {
      if (session && session.productId) emit('product_view');
    });
    return initialization;
  }

  function eventId(type) {
    var suffix = window.crypto && typeof window.crypto.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : Date.now() + '-' + Math.random().toString(36).slice(2);
    return 'theme:' + type + ':' + suffix;
  }

  function emit(type) {
    if (!EVENT_TYPES[type]) return Promise.resolve();
    if (emitted[type]) return emitted[type];
    emitted[type] = start().then(function (session) {
      if (!session) return null;
      return portal('/api/creator/attribution/events', {
        method: 'POST',
        keepalive: type === 'checkout_start',
        body: {
          eventId: eventId(type),
          eventType: type,
          creatorHandle: session.creatorHandle || undefined,
          productId: session.productId || undefined,
          browserClass: session.browserClass || undefined
        }
      }).catch(function () { return null; });
    });
    return emitted[type];
  }

  function requestUrl(input) {
    try {
      if (typeof input === 'string') return new URL(input, window.location.origin);
      if (input && typeof input.url === 'string') return new URL(input.url, window.location.origin);
    } catch (e) { /* not a URL the bridge owns */ }
    return null;
  }

  function isCartAdd(url, init) {
    return !!url && url.origin === window.location.origin && /\/cart\/add(?:\.js)?$/.test(url.pathname) &&
      (!init || !init.method || String(init.method).toUpperCase() === 'POST');
  }

  // A fast add immediately after a tracked landing waits for the opaque cart
  // token write. Untracked shoppers take the native fetch path unchanged.
  window.fetch = function (input, init) {
    var url = requestUrl(input);
    if (!isCartAdd(url, init) || (!refCode() && !hasHint())) {
      return nativeFetch(input, init);
    }
    return start().catch(function () { return null; }).then(function () {
      return nativeFetch(input, init);
    }).then(function (response) {
      if (response && response.ok) emit('cart_start');
      return response;
    });
  };

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || typeof form.getAttribute !== 'function') return;
    var action = form.getAttribute('action') || '';
    if (/\/cart\/add(?:\.js)?(?:\?|$)/.test(action)) emit('cart_start');
    if (/\/checkout(?:\?|$)/.test(action)) emit('checkout_start');
  }, true);

  document.addEventListener('click', function (event) {
    var target = event.target && event.target.closest
      ? event.target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]')
      : null;
    if (target) emit('checkout_start');
  }, true);

  start();
})();
